// --------------------------------------------------------------
// Created On: 2022-06-21
// Author: Zachary Thomas
//
// Last Modified: 2025-04-25
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useMemo } from "react";
import Chart from "react-apexcharts";
import formatDateShortLocal from "../../utilities/time/formatDateShortLocal";
import { ApexOptions } from "apexcharts";
import { BREAKPOINT_SMALL } from "../../constants/breakpoints";
import {
  GRAPH_DEFAULT_DECIMAL_PLACES_TO_ROUND_TO,
  GRAPH_MAX_VALUE_DIFF_TO_DROP_DECIMAL_SCALE,
} from "../../constants/miscellaneous";
import PropTypes from "prop-types";
import getSeriesUnitMap from "../../utilities/graphing/getSeriesUnitMap";
import getSeriesWillDrawYAxis from "../../utilities/graphing/getSeriesWillDrawYAxis";
import getSeriesYAxisColor from "../../utilities/graphing/getSeriesYAxisColor";
import getSeriesThatShareYAxis from "../../utilities/graphing/getSeriesThatShareYAxis";
import getShouldYAxesHaveBorders from "../../utilities/graphing/getShouldYAxesHaveBorders";

// Line chart for viewing data.
export default function LineChart(props: Props): Component {
  const options = useMemo(
    () => getOptions(props.series as TimeDataSeries[], props.axisMode),
    [JSON.stringify(props.series), props.axisMode]
  );

  // Get download file name based on if there are multiple series or not.
  function getFileName(series: TimeDataSeries[]): string {
    if (series.length === 1) {
      return `${series[0].name} History`;
    } else {
      return "Attribute History";
    }
  }

  // Get the options needed to configure the line chart.
  function getOptions(series: TimeDataSeries[], axisMode: AxisMode): ApexOptions | null {
    const yAxes: ApexYAxis[] = [];
    const seriesUnitMap = getSeriesUnitMap(series);
    const yAxesHaveBorders = getShouldYAxesHaveBorders(series, axisMode);

    series.forEach((singleSeries, i) => {
      const currentSeriesDrawsYAxis = getSeriesWillDrawYAxis(i, singleSeries.unitName, seriesUnitMap, axisMode);
      const seriesColor = getSeriesYAxisColor(i, singleSeries.unitName, seriesUnitMap, axisMode, series.length);
      const seriesThatShareYAxis = getSeriesThatShareYAxis(i, singleSeries.unitName, seriesUnitMap, axisMode, series);
      const maxValue = verticalBorderMax(seriesThatShareYAxis);
      const minValue = verticalBorderMin(seriesThatShareYAxis);

      yAxes.push({
        show: currentSeriesDrawsYAxis,
        seriesName: singleSeries.name,
        opposite: i % 2 !== 0,
        showAlways: axisMode === "Y_AXIS_PER_UNIT",
        max: maxValue,
        min: minValue,
        labels: {
          formatter: (value) => {
            let decimalPlaceToRoundTo = GRAPH_DEFAULT_DECIMAL_PLACES_TO_ROUND_TO;
            if (maxValue - minValue > GRAPH_MAX_VALUE_DIFF_TO_DROP_DECIMAL_SCALE) {
              decimalPlaceToRoundTo = 0;
            }
            let roundedValue: number;
            if (typeof value === "string") {
              roundedValue = parseFloat(parseFloat(value).toFixed(decimalPlaceToRoundTo));
            } else {
              roundedValue = parseFloat(value.toFixed(decimalPlaceToRoundTo));
            }
            return `${roundedValue} ${singleSeries.unitShortName === null ? "" : singleSeries.unitShortName}`;
          },
          style: {
            colors: seriesColor,
          },
        },
        axisBorder: {
          show: yAxesHaveBorders,
          color: seriesColor,
        },
      });
    });

    // Support cached data annotations.
    const pointAnnotations: PointAnnotations[] = [];
    if (props.series.length > 0 && props.series[0].dataMarkers !== undefined) {
      props.series[0].dataMarkers.forEach((marker) => {
        // Get timestamp.
        const timestamp = props.series[0].data[marker.dataPointIndex][0];

        // Don't allow value to be a string.
        let value = props.series[0].data[marker.dataPointIndex][1];
        if (typeof value === "string") {
          value = parseFloat(value);
          if (typeof value === "string") {
            value = 0;
          }
        }

        pointAnnotations.push({
          x: timestamp,
          y: value,
          marker: {
            size: 6,
            fillColor: "var(--white)",
            strokeColor: "var(--bs-warning)",
            radius: 2,
          },
        });
      });
    }

    // Set general options.
    if (series !== null && series.length > 0) {
      return {
        chart: {
          height: 500,
          type: "line",
          zoom: {
            enabled: true,
            type: "x",
            autoScaleYaxis: false,
          },
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: false,
              zoomout: false,
              pan: true,
              reset: true,
            },
            export: {
              csv: {
                filename: getFileName(props.series as TimeDataSeries[]),
                columnDelimiter: ",",
                headerCategory: `${Intl.DateTimeFormat().resolvedOptions().timeZone} Time`,
                headerValue: "value",
                dateFormatter(timestamp: number | undefined) {
                  if (timestamp === undefined) {
                    return "";
                  } else {
                    const date = new Date(timestamp);
                    return formatDateShortLocal(date.toISOString().split(".")[0]).replace(",", "");
                  }
                },
              },
              svg: {
                filename: getFileName(props.series as TimeDataSeries[]),
              },
              png: {
                filename: getFileName(props.series as TimeDataSeries[]),
              },
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: "straight",
        },
        grid: {
          row: {
            colors: ["var(--chart-background)", "var(--chart-background-alternative)"],
            opacity: 1,
          },
          padding: {
            left: 5,
            right: 0,
          },
        },
        xaxis: {
          type: "datetime",
          tickAmount: 12,
          labels: {
            datetimeUTC: true,
            rotate: -15,
            rotateAlways: true,
            formatter: (value: string) => {
              const date = new Date(value);
              return formatDateShortLocal(date.toISOString().split(".")[0]);
            },
          },
        },
        yaxis: yAxes,
        tooltip: {
          shared: true,
        },
        annotations: {
          points: pointAnnotations,
        },
        legend: {
          onItemClick: {
            toggleDataSeries: false,
          },
        },
        responsive: [
          {
            breakpoint: BREAKPOINT_SMALL,
            options: {
              yaxis: {
                show: false,
                labels: {
                  offsetX: 0,
                },
              },
              grid: {
                row: {
                  colors: ["var(--chart-background)", "var(--chart-background-alternative)"],
                  opacity: 1,
                },
                padding: {
                  left: -100,
                  right: -100,
                },
              },
            },
          },
        ],
      };
    } else {
      return null;
    }
  }

  // Calculate max y-axis border value.
  function verticalBorderMax(series: TimeDataSeries[]): number {
    let maxValue: number | null = null;
    // Find the max value.
    series.forEach((singleSeries) => {
      singleSeries.data.forEach((dataPoint) => {
        if (dataPoint.length > 1) {
          let value = dataPoint[1];
          if (typeof value === "string") {
            value = parseFloat(value);
          }
          if (maxValue === null || value > maxValue) {
            maxValue = value;
          }
        }
      });
    });

    // Add a buffer to the max value.
    if (maxValue === null) {
      maxValue = 1;
    } else if (maxValue >= -0.00000001 && maxValue < 0.00000001) {
      maxValue = 0.25;
    } else {
      maxValue = parseFloat(
        ((maxValue as number) + Math.abs(maxValue) * 0.25).toFixed(GRAPH_DEFAULT_DECIMAL_PLACES_TO_ROUND_TO)
      );
    }

    return maxValue;
  }

  // Calculate min y-axis border value.
  function verticalBorderMin(series: TimeDataSeries[]): number {
    let minValue: number | null = null;
    // Find the min value.
    series.forEach((singleSeries) => {
      singleSeries.data.forEach((dataPoint) => {
        if (dataPoint.length > 1) {
          let value = dataPoint[1] as number | string;
          if (typeof value === "string") {
            value = parseFloat(value);
          }
          if (minValue === null || value < minValue) {
            minValue = value;
          }
        }
      });
    });

    // Add a buffer to the min value.
    if (minValue === null) {
      minValue = 0;
    } else if (minValue <= 0.00000001 && minValue > -0.00000001) {
      minValue = -0.25;
    } else {
      const offset = parseFloat(
        (minValue - Math.abs(minValue) * 0.25).toFixed(GRAPH_DEFAULT_DECIMAL_PLACES_TO_ROUND_TO)
      );
      if (minValue > 0 && offset < 0) {
        minValue = 0;
      } else {
        minValue = offset;
      }
    }

    return minValue;
  }

  return options ? (
    <Chart
      options={options}
      series={props.series === null ? [] : (props.series as TimeDataSeries[])}
      type="line"
      height={500}
    />
  ) : null;
}

LineChart.propTypes = {
  series: PropTypes.array.isRequired,
  axisMode: PropTypes.string.isRequired,
};

interface Props {
  series: VariousTimeDataSeries[];
  axisMode: AxisMode;
}

interface VariousTimeDataSeries {
  name: string;
  unitName: string;
  unitShortName: string;
  data: [number, number | string][];
  dataMarkers?: DataMarker[];
}

interface DataMarker {
  seriesIndex: number;
  dataPointIndex: number;
}

interface TimeDataSeries {
  name: string;
  unitName: string;
  unitShortName: string;
  data: [number, number][];
}

type AxisMode = "Y_AXIS_PER_ATTRIBUTE" | "Y_AXIS_PER_UNIT";
